import { X } from "lucide-react";
import { useT } from "@/i18n";
import type { FilterSet } from "@/hooks/useFilterSet";
import { Button } from "../button";

/**
 * What the view is filtered to, one dismissable chip per **value**.
 *
 * Additive to the filter button's count badge rather than a replacement for it: the badge says
 * how many *filters* are on, the chips say which values, and only the chips let a reader undo
 * one without opening the panel and hunting for the control that set it.
 *
 * **Per value since 2026-08-26.** It was one chip per key naming every value —
 * `Stage: Mutual Interest, Discovery, Validation, Decide and Close` — on the reasoning that a
 * chip per value would multiply the row by the number of options. The reasoning held and the
 * behaviour was still wrong: that chip's ✕ cleared all four, so correcting one choice threw
 * away three the reader still wanted. The row does grow with the values; what does not grow is
 * the *key*, which is stated once above its group instead of once inside every chip.
 *
 * **No hue per filter.** The suggestion was to tell the groups apart by colour, and it is
 * declined: stages and managers already own colour on these screens — stage colours are
 * configurable, manager colours paint the cards — so a Stage chip tinted violet beside a
 * Discovery stage that *is* violet would read as that stage's colour. The caption and the
 * grouping do the separating, and colour stays where it already means something.
 */
export function ActiveFilterChips({ filters }: { filters: FilterSet }): React.JSX.Element | null {
  const t = useT();

  if (filters.activeFilterGroups.length === 0) return null;

  return (
    <div className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
      {filters.activeFilterGroups.map((group) => (
        <div key={group.key} className="min-w-0">
          {/* The key, once. Uppercase and small: it is a caption for the pills under it, not
              another thing to read at their size. A search term and a bool have no caption —
              the term is the whole statement, and a bool's name is its on state. */}
          {group.label && (
            <span className="text-muted-foreground mb-1 block text-[10px] font-medium tracking-wide uppercase">
              {group.label}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            {group.values.map((entry) => (
              <span
                key={entry.value}
                className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full py-0.5 pr-0.5 pl-2.5 text-xs font-medium"
              >
                {entry.label}
                <button
                  type="button"
                  // Named in full — the *filter* and the value, not just the value. A screen
                  // reader hears these as a list, and "Remove Discovery" five times over says
                  // nothing about which filter is losing it.
                  aria-label={t(
                    "table.removeFilter",
                    group.label ? `${group.label}: ${entry.label}` : entry.label,
                  )}
                  onClick={() => filters.removeValue(group.key, entry.value)}
                  className="hover:bg-primary/20 focus-visible:ring-ring rounded-full p-0.5 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        </div>
      ))}

      {/* Clear-all earns its place only once undoing them one at a time is a chore. Counted on
          filters rather than on chips: two filters holding five values between them is still
          two things to undo by hand. */}
      {filters.activeFilters.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={filters.clearFilters}
          className="text-muted-foreground h-6 px-2 text-xs"
        >
          {t("table.clearAll")}
        </Button>
      )}
    </div>
  );
}
